import {
  Box,
  type BoxProps,
  type ButtonProps,
  PopoverBody,
  type PopoverBodyProps,
  Flex,
  type FlexProps,
  forwardRef,
} from '@chakra-ui/react-v2';

import { type SortDirection } from '@frontend/legacy-components';
import {
  SortAscendingIcon,
  SortDescendingIcon,
} from '@frontend/legacy-components/src/components/mediaAndIcons/icons/iconSet';

export type TableFilterPopoverBodyProps = PopoverBodyProps;

export const TableFilterPopoverBody = forwardRef<
  TableFilterPopoverBodyProps,
  'div'
>(({ children, ...restProps }, ref) => {
  return (
    <PopoverBody
      ref={ref}
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap={1}
      pb={1.5}
      {...restProps}
    >
      {children}
    </PopoverBody>
  );
});
TableFilterPopoverBody.displayName = 'TableFilterPopoverBody';

type TableFilterPopoverSortButtonProps = BoxProps &
  ButtonProps & {
    isDisabled: boolean;
    sortDirection?: SortDirection;
    selected: boolean;
  };

// Internal component used within the TableFilterPopoverSortButtons.
const TableFilterPopoverSortButton = ({
  isDisabled,
  sortDirection = 'ascending',
  selected,
  ...restProps
}: TableFilterPopoverSortButtonProps) => {
  return (
    <Box
      as="button"
      disabled={isDisabled}
      h={2.5}
      px={0.25}
      display="flex"
      flexDirection="row"
      alignItems="center"
      flexGrow={1}
      gap={0.5}
      borderRadius="sm"
      fontFamily="default"
      fontSize="sm"
      lineHeight="sm"
      transitionDuration="normal"
      transitionProperty="common"
      bg={selected ? 'interaction.main' : 'page.plateLight'}
      color={selected ? 'feature.onInteraction' : 'feature.primary'}
      _hover={{
        bg: selected ? 'interaction.hover' : 'interaction.surfaceHover',
      }}
      _active={{
        bg: selected ? 'interaction.active' : 'interaction.surfaceActive',
      }}
      outline="none"
      _focusVisible={{ boxShadow: 'focus' }}
      _disabled={{
        bg: 'interaction.surfaceDisabled',
        color: 'feature.placeholder',
        cursor: 'not-allowed',
      }}
      {...restProps}
    >
      {sortDirection === 'ascending' ? (
        <>
          <SortAscendingIcon
            boxSize="1.125rem"
            color={selected || isDisabled ? 'inherit' : 'feature.secondary'}
          />
          Ascending
        </>
      ) : (
        <>
          <SortDescendingIcon
            boxSize="1.125rem"
            color={selected || isDisabled ? 'inherit' : 'feature.secondary'}
          />
          Descending
        </>
      )}
    </Box>
  );
};

export interface TableFilterPopoverSortButtonsProps
  extends Omit<FlexProps, 'onChange'> {
  value: SortDirection;
  sortPossibleDirections: SortDirection[];
  onChange: (value: SortDirection) => void;
}

export const TableFilterPopoverSortButtons = forwardRef<
  TableFilterPopoverSortButtonsProps,
  'div'
>(({ value, onChange, sortPossibleDirections = [], ...restProps }, ref) => {
  return (
    <Flex ref={ref} gap={1} pb={0.5} {...restProps}>
      <TableFilterPopoverSortButton
        type="button"
        sortDirection="ascending"
        selected={value === 'ascending'}
        isDisabled={!sortPossibleDirections.includes('ascending')}
        onClick={() => {
          if (value !== 'ascending') {
            onChange('ascending');
          }
        }}
      />
      <TableFilterPopoverSortButton
        type="button"
        sortDirection="descending"
        selected={value === 'descending'}
        isDisabled={!sortPossibleDirections.includes('descending')}
        onClick={() => {
          if (value !== 'descending') {
            onChange('descending');
          }
        }}
      />
    </Flex>
  );
});
TableFilterPopoverSortButtons.displayName = 'TableFilterPopoverSortButtons';
